import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { getGlobalSearchQueryURL } from "../../constants";

// --- Facet group APIs  ---
const API_BASE = getGlobalSearchQueryURL;
const groupToApi = (group, locale) =>
  `${API_BASE}?q=*&locale=${locale}&fl=id&groupBy=${group}&groupLimit=10`;
const FACETS_API = (locale) =>
  // `${API_BASE}?q=*&locale=${locale}&fl=id&groupBy=filterProductCategoryType,filterSubDocumentType,filterIndustryType&groupLimit=10`;
  `${API_BASE}?q=*&locale=${locale}&fl=id,title,url,description,documentTypeDate,documentType&sort=documentTypeDate&from=0&rows=10&groupBy=filterProductCategoryType,filterSubDocumentType,filterIndustryType&groupLimit=10`;

function humanizeFacetKey(k) {
  return String(k || "")
    .replace(/^filter/i, "")
    .replace(/Type$/i, " Type")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .trim()
    .replace(/\b\w/g, s => s.toUpperCase());
}
function getGroupLabel(key, t) {
  const path = `resource.groupLabel.${key}`;
  const fromI18n = t(path);
  if (fromI18n && fromI18n !== path) return fromI18n;
  const fallbacks = {
    filterProductCategoryType: "Category",
    filterProductType: "Product",
    filterSubDocumentType: "Document Type",
    filterIndustryType: "Industry",
  };
  return fallbacks[key] || humanizeFacetKey(key);
}
function buildSolrFq(filtersObj) {
  const fqs = [];
  Object.entries(filtersObj || {}).forEach(([group, values]) => {
    const cleanVals = (Array.isArray(values) ? values : [])
      .map(v => (v ?? "").trim())
      .filter(Boolean);

    if (cleanVals.length === 1) {
      fqs.push(`fq=${group}:${encodeURIComponent(cleanVals[0])}`);
    } else if (cleanVals.length > 1) {
      const joined = cleanVals.map(v => encodeURIComponent(v)).join(",");
      fqs.push(`fq=${group}:${joined}`);
    }


  });
  return fqs.join("&");
}
function buildSolrFqExcept(filtersObj, exceptKey) {
  const fqs = [];
  Object.entries(filtersObj || {}).forEach(([group, values]) => {
    if (group === exceptKey) return;
    const cleanVals = (Array.isArray(values) ? values : [])
      .map(v => (v ?? "").trim())
      .filter(Boolean);
    if (cleanVals.length === 1) {
      fqs.push(`fq=${group}:${encodeURIComponent(cleanVals[0])}`);
    } else if (cleanVals.length > 1) {
      const joined = cleanVals.map(v => encodeURIComponent(v)).join(",");
      fqs.push(`fq=${group}:${joined}`);
    }
  });
  return fqs.join("&");
}

// --- Breadcrumb Component ---
function Breadcrumb() {
  const { t } = useTranslation();
  return (
    <nav className="defaultBreadcrumbView my-6 flex flex-wrap items-center" aria-label="Breadcrumb">
      <ol className="flex flex-wrap items-center" itemScope itemType="https://schema.org/BreadcrumbList">
        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="flex flex-wrap items-center">
          <a itemProp="item" href="https://www.westerndigital.com"
            className="text-sm text-grey-3 hover:text-black hover:underline inline-block" data-track-type="breadcrumb">
            <span itemProp="name">{t("resource.breadcrumb_home")}</span>
          </a>
          <meta itemProp="position" content="1" />
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
            <path d="M0,0H21V21H0Z" fill="none"></path>
            <path d="M11.572,9.943l-3.35-3.35.957-.957,4.307,4.307L9.179,14.251l-.957-.957Z" transform="translate(-0.028 0.818)" fill="#6a6a6a"></path>
          </svg>
        </li>
        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="flex flex-wrap items-center">
          <a itemProp="item" href="/support"
            className="text-sm text-grey-3 hover:text-black hover:underline inline-block" data-track-type="breadcrumb">
            <span itemProp="name">{t("resource.breadcrumb_support")}</span>
          </a>
          <meta itemProp="position" content="2" />
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
            <path d="M0,0H21V21H0Z" fill="none"></path>
            <path d="M11.572,9.943l-3.35-3.35.957-.957,4.307,4.307L9.179,14.251l-.957-.957Z" transform="translate(-0.028 0.818)" fill="#6a6a6a"></path>
          </svg>
        </li>
        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem" className="flex flex-wrap items-center">
          <span itemProp="name" className="text-sm text-black font-bold">{t("resource.breadcrumb_current")}</span>
          <meta itemProp="position" content="3" />
        </li>
      </ol>
    </nav>
    
  );
}

// --- Pagination Component ---
function Pagination({ page, totalPages, onChange }) {
  const { t } = useTranslation();
  if (!totalPages || totalPages <= 1) return null;

  const go = (p) => {
    if (p < 1 || p > totalPages || p === page) return;
    onChange(p);
  };

  const makeRange = (count, current, siblingCount = 1) => {
    const totalNumbers = siblingCount * 2 + 5;
    if (count <= totalNumbers) return Array.from({ length: count }, (_, i) => i + 1);
    const left = Math.max(2, current - siblingCount);
    const right = Math.min(count - 1, current + siblingCount);
    const showLeftDots = left > 2;
    const showRightDots = right < count - 1;
    const arr = [1];
    if (showLeftDots) arr.push("…");
    for (let p = left; p <= right; p++) arr.push(p);
    if (showRightDots) arr.push("…");
    arr.push(count);
    return arr;
  };

  const items = makeRange(totalPages, page, 1);

  return (
    <nav aria-label="Pagination" className="clp-pagination-wrap my-10">
      <div className="mt-8 xs:mt-12 sm:flex justify-center items-center">
        <ul className="list-reset flex items-center justify-center relative sm:w-[500px] md:w-full">
          {/* PREV (left) */}
          <button
            id="prevbtn"
            onClick={() => go(page - 1)}
            disabled={page <= 1}
            className="new-prev-2 btnprev md:pr-12 pr-80% flex items-center absolute lg:relative left-0 -top-6 -pin-t-6 lg:top-0 lg:pr-3
                       disabled:wd-opacity-40 disabled:wd-cursor-not-allowed disabled:wd-pointer-events-none focus:wd-outline focus:wd-outline-2 focus:wd-outline-[#c59d28]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" className="mr-1 rotate-180">
              <path d="M0,0H24V24H0Z" fill="none"></path>
              <path d="M17.818,23.015,12.7,17.875l.725-.725,4.393,4.393,4.393-4.393.725.747Z" transform="translate(-7.143 29.7) rotate(-90)" stroke="currentColor"></path>
            </svg>
            <span className="pagination-prev font-medium leading-tight">{t("pagination.prev")}</span>
          </button>

          {items.map((it, idx) =>
            it === "…" ? (
              <li key={`dots-${idx}`} className="dots mt-1 sm:mt-0 md:mx-3 mx-1 cursor-default">
                <span className="h-12 w-12 inline">...</span>
              </li>
            ) : (
              <li key={it} className="mt-1 sm:mt-0 md:mx-3 -mx-1 xs:mx-1">
                <button
                  aria-current={it === page ? "page" : undefined}
                  onClick={() => go(it)}
                  className={`flex justify-center numbers leading-loose inline-block wd-rounded-full cursor-pointer
                              h-12 w-12 px-5 py-2 hover:wd-bg-[#f2f3f3] focus:wd-outline focus:wd-outline-2 focus:wd-outline-[#c59d28]
                              ${it === page ? "active wd-rounded-full wd-bg-[#f2f3f3]" : ""}`}
                  title={`Page ${it}`}
                >
                  {it}
                </button>
              </li>
            )
          )}

          {/* NEXT (right) */}
          <button
            id="nextbtn"
            onClick={() => go(page + 1)}
            disabled={page >= totalPages}
            className="btnnext md:pl-12 pl-80% flex items-center absolute lg:relative right-0 -top-6 -pin-t-6 lg:top-0 lg:pl-3
                       disabled:wd-opacity-40 disabled:wd-cursor-not-allowed disabled:wd-pointer-events-none focus:wd-outline focus:wd-outline-2 focus:wd-outline-[#c59d28]"
          >
            <span className="font-medium leading-tight mr-1">{t("pagination.next")}</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
              <path d="M0,0H24V24H0Z" fill="none"></path>
              <path d="M17.818,23.015,12.7,17.875l.725-.725,4.393,4.393,4.393-4.393.725.747Z" transform="translate(-7.143 29.7) rotate(-90)" stroke="currentColor"></path>
            </svg>
          </button>
        </ul>
      </div>
    </nav>
  );
}

function sanitizeFacetFields(fields) {
  if (!Array.isArray(fields)) return [];
  return fields.map(group => {
    const k = Object.keys(group || {})[0];
    const arr = Array.isArray(group?.[k]) ? group[k] : [];
    const clean = arr.filter(it => (it?.docType ?? "").trim().length > 0);
    return { [k]: clean };
  });
}


// --- Main Component ---
export default function ResourceLibrary() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "en-us").toLowerCase();
  const [filterData, setFilterData] = useState(null);
  const [baseFacets, setBaseFacets] = useState([]);
  const [filters, setFilters] = useState(null);
  const [accordionOpen, setAccordionOpen] = useState(null);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const headingRef = useRef(null);
  const SCROLL_OFFSET = 0; 

  const scrollToHeading = () => {
    if (!headingRef.current) return;
    const rect = headingRef.current.getBoundingClientRect();
    const top = rect.top + window.scrollY - SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
  };

const [page, setPage] = useState(1);
const [totalResults, setTotalResults] = useState(0);
const resultsPerPage = 10;
const totalPages = Math.max(1, Math.ceil(totalResults / resultsPerPage));

useEffect(() => {
  setPage((p) => Math.min(p, totalPages) || 1);
}, [totalPages]);

useEffect(() => { setPage(1); }, [filters, appliedSearch]);
const from = totalResults === 0 ? 0 : (page - 1) * resultsPerPage + 1;
const to = Math.min(page * resultsPerPage, totalResults);

  useEffect(() => {
    fetch(FACETS_API(locale))
      .then((res) => res.json())
      .then((data) => {
      const fields = sanitizeFacetFields(data.facet_fields || []);
      const order = ["filterProductCategoryType","filterProductType","filterSubDocumentType","filterIndustryType"];
      const map = Object.fromEntries(fields.map(g => [Object.keys(g)[0], g[Object.keys(g)[0]]]));
      const ordered = order.filter(k => Array.isArray(map[k])).map(k => ({ [k]: map[k] }));
      setBaseFacets(ordered);
      setFilterData(ordered);
    });
  }, [locale]);

  useEffect(() => {
    if (!Array.isArray(filterData) || filterData.length === 0) {
      setAccordionOpen(null);
      setFilters(null);
      return;
    }
    if (filters === null) {
      const groupKeys = filterData.map((g) => Object.keys(g)[0]);
      setAccordionOpen(Object.fromEntries(groupKeys.map((k, idx) => [k, idx === 0])));
      setFilters(Object.fromEntries(groupKeys.map((k) => [k, []])));
    }
  }, [filterData, filters]);

  useEffect(() => {
    if (!filters || typeof filters !== "object" || Object.keys(filters).length === 0) return;

    const controller = new AbortController();
    const fqString = buildSolrFq(filters);
    const qString = appliedSearch ? `q=${encodeURIComponent(appliedSearch)}` : "q=*";
    const start = (page - 1) * resultsPerPage;
    const rows = resultsPerPage;
    const hasCategoryFilter = Array.isArray(filters?.filterProductCategoryType) && filters.filterProductCategoryType.length > 0;
    const baseUrl = `${API_BASE}?${qString}&locale=${locale}&fl=id,title,url,description,documentTypeDate&sort=documentTypeDate${fqString ? "&" + fqString : ""}&from=${start}&rows=${rows}`;
    const fqNoProduct = buildSolrFqExcept(filters, "filterProductType");
    const apiUrl = baseUrl;
    const facetUrl = hasCategoryFilter
      ? `${API_BASE}?${qString}&locale=${locale}&fl=id&sort=documentTypeDate&groupBy=filterProductType&groupLimit=100${fqNoProduct ? "&" + fqNoProduct : ""}`
      : null;

    setLoading(true);
    Promise.all([
      fetch(apiUrl, { signal: controller.signal }).then(r => r.json()),
      facetUrl ? fetch(facetUrl, { signal: controller.signal }).then(r => r.json()) : Promise.resolve(null),
    ]).then(([data, facetData]) => {
        setResults(data.docs || []);
        setTotalResults((typeof data.total === "number" ? data.total : data.numFound) ?? 0);

        const productList = Array.isArray(facetData?.facet_fields?.filterProductType)
        ? facetData.facet_fields.filterProductType.filter(it => (it?.docType ?? "").trim().length > 0)
        : [];
        
        setFilterData(prev => {
          const prevArrRaw = Array.isArray(prev) && prev.length ? prev : baseFacets;
          const hasPrevProduct = Array.isArray(prevArrRaw) && prevArrRaw.some(g => Object.keys(g)[0] === "filterProductType");

          if (hasCategoryFilter && productList.length) {
            const base = prevArrRaw.filter(g => Object.keys(g)[0] !== "filterProductType");
            const out = [];
            let inserted = false;
            for (const g of base) {
              out.push(g);
              if (!inserted && Object.keys(g)[0] === "filterProductCategoryType") {
                out.push({ filterProductType: productList });
                inserted = true;
              }
            }
            const next = inserted ? out : [{ filterProductType: productList }, ...base];
            return JSON.stringify(next) === JSON.stringify(prevArrRaw) ? prevArrRaw : next;
          }

          if (!hasPrevProduct) return prevArrRaw;
          const next = prevArrRaw.filter(g => Object.keys(g)[0] !== "filterProductType");
          return JSON.stringify(next) === JSON.stringify(prevArrRaw) ? prevArrRaw : next;
        });
        setAccordionOpen(prev => {
          const next = { ...(prev || {}) };
          if (hasCategoryFilter && productList.length) {
            next.filterProductType = true;
          } else {
            delete next.filterProductType;
          }
          return next;
        });
        if (!hasCategoryFilter) {
          setFilters(prev => {
            if (!prev || !Array.isArray(prev.filterProductType) || prev.filterProductType.length === 0) return prev;
            return { ...prev, filterProductType: [] };
          });
        }
      })
      .catch(err => {
        if (err.name !== "AbortError") console.error(err);
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  }, [filters, appliedSearch, page, locale]);

  const toggleAccordion = (key) => {
    setAccordionOpen((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleFilterChange = (groupKey, docType) => {
    setFilters((prev) => {
      if (!prev) return prev;
      const arr = prev[groupKey] || [];
      const already = arr.includes(docType);
      const updated = {
        ...prev,
        [groupKey]: already
          ? arr.filter((x) => x !== docType)
          : [...arr, docType],
      };
      fetch(groupToApi(groupKey, locale))
        .then(res => res.json())
        .then(data => {
          const facetField = sanitizeFacetFields(data.facet_fields || [])[0];
          if (facetField && Array.isArray(filterData)) {
            setFilterData((oldFields) =>
              (oldFields || []).map((field) =>
                Object.keys(field)[0] === groupKey ? facetField : field
              )
            );
          }
        });
      return updated;
    });
  };

  const removeChip = (groupKey, value) => {
    setFilters((prev) => {
      if (!prev) return prev;
      return ({
        ...prev,
        [groupKey]: Array.isArray(prev[groupKey])
          ? prev[groupKey].filter((x) => x !== value)
          : [],
      });
    });
  };

  const removeSearchChip = () => {
    setAppliedSearch("");
    setSearch("");
  };

  const clearAll = () => {
    if (Array.isArray(filterData) && filterData.length) {
      const groupKeys = filterData.map((g) => Object.keys(g)[0]);
      setFilters(Object.fromEntries(groupKeys.map((k) => [k, []])));
    } else {
      setFilters({});
    }
    setAppliedSearch("");
    setSearch("");
    setPage(1);
  };

   const [showFilter, setShowFilter] = useState(false);
    useEffect(() => {
      if (showFilter) {
          document.body.classList.add("overflow-hidden");
        } else {
        document.body.classList.remove("overflow-hidden");
      }

      // cleanup on unmount
      return () => {
        document.body.classList.remove("overflow-hidden");
      };
    }, [showFilter]);

  const SearchIcon = (
    <svg aria-hidden="true" width="15" height="20" viewBox="0 0 57 58" fill="none" stroke="currentColor" strokeWidth="5" xmlns="http://www.w3.org/2000/svg" className="searchmag rotate-90">
      <path d="M42.9302 23.2003C42.9302 34.6828 33.83 43.9006 22.7151 43.9006C11.6001 43.9006 2.5 34.6828 2.5 23.2003C2.5 11.7178 11.6001 2.5 22.7151 2.5C33.83 2.5 42.9302 11.7178 42.9302 23.2003Z"></path>
      <line y1="-2.5" x2="27.0574" y2="-2.5" transform="translate(34.0703 38.6664) scale(0.989377 1.01051) rotate(45)"></line>
    </svg>
  );
  const sidebar =
    Array.isArray(filterData) && filterData.length && filters && typeof filters === "object"
      ? filterData.map(group => {
          if (!group || typeof group !== "object") return null;
          const keys = Object.keys(group);
          if (!Array.isArray(keys) || keys.length === 0) return null;
          const key = keys[0];
          const items = Array.isArray(group[key]) ? group[key] : [];
          const visibleItems = items.filter(({ docType }) => (docType ?? "").trim().length > 0);
          const isOpen = !!(accordionOpen && typeof accordionOpen === "object" && accordionOpen[key] === true);
          const label = getGroupLabel(key, t);

          return (
            <div key={key} className="accordion-text mb-6 pb-6 border-b border-gray-light">
              <button
                className="flex items-center justify-between font-bold text-left text-base py-2 uppercase bg-transparent sm:px-2 px-5 w-full"
                type="button"
                onClick={() => toggleAccordion(key)}
                style={{ border: "none" }}
              >
                <span className="text-xs tracking-wide font-semibold text-gray-800">
                  {t("resource.filter_by", { label })}
                </span>
                <span className="ml-2 text-xl font-bold text-gray-800 select-none" style={{ lineHeight: 1 }}>
                  {isOpen ? "–" : "+"}
                </span>
              </button>
              {isOpen && (
                <ul className="mt-1 space-y-1 max-h-56 overflow-y-auto pr-1 sm:pl-0 pl-4">
                  {visibleItems.length
                    ? visibleItems.map(({ docType }) => (
                        <li key={docType || "empty"}>
                          <label className="checkbox flex items-center ml-1 relative cursor-pointer text-[16px] font-normal leading-tight min-h-[32px] select-none">
                            <span className="ml-2 flex leading-tight ml-4">{docType}</span>
                            <input
                              type="checkbox"
                              className="absolute opacity-0 h-0 w-0 peer"
                              checked={filters?.[key]?.includes(docType)}
                              onChange={() => handleFilterChange(key, docType)}
                              value={docType}
                              id={`selectedCategory-${docType}`}
                            />
                            <span className="checkmark h-[18px] w-[18px] border border-gray-400 rounded-[3px] bg-white ml-2 flex-shrink-0 peer-checked:bg-blue-600 peer-checked:border-blue-600 flex items-center justify-center transition">
                              <svg className="hidden peer-checked:block" width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M1 5L5 9L12 1" stroke="white" strokeWidth="2" />
                              </svg>
                            </span>
                          </label>
                        </li>
                      ))
                    : <li className="text-xs text-gray-500 px-2 py-1">—</li>}
                </ul>
              )}
            </div>
          );
        })
      : null;
  const filterChips =
    filters && typeof filters === "object"
      ? Object.entries(filters).flatMap(([group, values]) =>
          Array.isArray(values)
            ? values.map(value => (
                <span key={group + value} className="inline-flex items-center bg-gray-200 rounded px-2 py-1 text-xs font-semibold">
                  {value}
                  <button className="ml-1 text-gray-600 hover:text-black" onClick={() => removeChip(group, value)}>
                    ×
                  </button>
                </span>
              ))
            : []
        )
      : [];

  return (
    <section className="min-h-screen bg-[#f8f9fa] py-8 flex justify-center contain resourceLibraryComp">
      <div className="w-full max-w-[1400px] flex gap-0 md:gap-8 flex-col">
        {/* Breadcrumb */}
        <Breadcrumb />
        {/*page tile */}
        <h1 ref={headingRef} className="my-6">{t("resource.title")}</h1>
        <div className="row grid-12 xs:grid-12 sm:grid-12 md:grid-12 lg:grid-12 xl:grid-12 flex-wrap flex md:-mx-6">
          {/* Filter Sidebar */}          
          <button className="sm:hidden flex-1 items-center mob-filter-button cursor-pointer w-full text-sm py-2 border-2 border-black text-center text-white bg-black mr-2 mb-12 uppercase" onClick={() => setShowFilter(true)}><span>Filter</span></button>          
          <aside className={`cols-12 sm:cols-4 md:cols-3 sm:px-6 fixed sm:relative pin-t pin-l z-40 sm:z-0 bg-white sm:bg-transparent sm:block h-full w-full ${!showFilter ? "hidden" : ""}`}>
            <div className="font-medium fliter_header block sm:hidden w-full text-lg py-2 text-center text-bold border-b border-gray-2 sm:mb-8 uppercase">
			        Filter
			        <button data-track-type="close filters" data-track-heading="close filters" aria-label="Close Filters" tabindex="0" className="close-btn float-right cursor-pointer mr-5 mt-2 absolute pin-r-0 sc-track" onClick={() => setShowFilter(false)}><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L7 7L13 13M13 1L1 13" stroke="black"></path></svg></button>
            </div>
            {/* Search Box */}
            <form className="mb-6 relative flex justify-end items-center" onSubmit={e => { e.preventDefault(); setAppliedSearch(search); }} autoComplete="off">
              <input
                className="w-full bg-transparent border-0 border-b border-gray focus:border-black focus:ring-0 py-3 px-0 sm:pl-2 pl-5 pr-10 text-sm placeholder-gray-400 transition rounded-none outline-none"
                type="text"
                id="seach-resource-input"
                name="seach-resource"
                placeholder={t("resource.type_to_search")}
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setAppliedSearch(search);
                  }
                }}
                style={{ boxShadow: "none" }}
              />
              <span
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer sm:pin-r-6 pin-r-20"
                onClick={() => setAppliedSearch(search)}
              >
                {SearchIcon}
              </span>
            </form>
            {/* Filter chips */}
            <div className="mb-2 flex flex-wrap gap-2 items-center">
              {appliedSearch && (
                <span className="inline-flex items-center bg-gray-200 rounded px-2 py-1 text-xs font-semibold">
                  {appliedSearch}
                  <button
                    className="ml-1 text-gray-600 hover:text-black"
                    onClick={removeSearchChip}
                    aria-label={t("resource.clear_search")}
                  >
                    ×
                  </button>
                </span>
              )}
              {filterChips}
              {(filterChips.length > 0 || appliedSearch) && (
                <button className="text-xs text-blue-700 underline ml-2" onClick={clearAll}>
                  {t("resource.clear_all")}
                </button>
              )}
            </div>
            {/* Facet Accordions */}
            <div className="h-full overflow-y-auto sm:h-auto rl_filter_wrap">
              {sidebar}
            </div>
          </aside>
          {/* Right content area */}
          <main className="cols-12 sm:cols-8 md:cols-9 md:px-6 min-h-[500px]">
            <div className="flex items-center justify-end mb-3">
              <div className="text-sm text-gray-600">
                {totalResults > 0 ? t("resource.range", { from, to, total: totalResults }) : t("resource.no_results")}
              </div>
            </div>

            <div className="space-y-4">
              {loading ? (
                <div className="bg-gray-1 bg-grey-1 p-6 mb-2">{t("common.loading")}</div>
              ) : results.length === 0 ? (
                <div className="bg-gray-1 bg-grey-1 p-6 mb-2">{t("resource.no_results_found")}</div>
              ) : (
                results.map((doc, idx) => (
                  <div key={doc.id || idx} className="bg-gray-1 bg-grey-1 p-6 mb-2">
                    <div className="text-xs text-gray-400 mb-1">
                      {doc.documentTypeDate
                        ? new Date(doc.documentTypeDate).toLocaleDateString(i18n.language, { month: "short", day: "numeric", year: "numeric" })
                        : ""}
                    </div>
                    <div className="font-bold text-base mb-1 break-words whitespace-normal" style={{ wordBreak: 'break-word' }}>
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        {(Array.isArray(doc.title) ? doc.title[0] : doc.title) || t("resource.no_title")}
                      </a>
                    </div>
                    {doc.description && (
                      <div className="text-sm text-gray-800 mb-1 break-words whitespace-normal" style={{ wordBreak: 'break-word' }}>
                        {doc.description}
                      </div>
                    )}
                    {/* Download row */}
                    <div className="mt-2 text-sm flex items-center gap-2">
                      <a href={doc.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center cursor-pointer text-xxs leading-normal">
                         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" height="16" width="20" className="-ml-1 mr-1"><path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"></path> <path d="M0 0h24v24H0z" fill="none"></path></svg>
                        {t("resource.download")}
                      </a>
                    </div>
                  </div>
                ))
              )}
            </div>
            {/* Pagination */}
            {totalResults > 0 && (
              <Pagination
                page={page}
                totalPages={totalPages}
                onChange={(p) => { 
                  setPage(p);
                  scrollToHeading();
                }}
              />
            )}
          </main>
        </div>
      </div>
    </section>
  );
}
